// The viewer's journey: per program, only THEIR sessions in sequence order
// (the backend already filtered to participant rows — nothing is derived here).
import { useT } from '../lib/i18n';
import React from 'react';
import { Card, CardContent, CardTitle } from '@/features/shared/components/ui/card';
import type { Journey, MySession } from '@/services/learningService';
import { formatSessionRange } from '../lib/format';
import { AssignmentTypeBadge } from './AssignmentTypeBadge';
import { AttendanceChip } from './AttendanceChip';
import { SessionStatusChip } from './SessionStatusChip';

export const TrainingJourney: React.FC<{ programs: Journey['programs']; onOpen: (s: MySession) => void }> = ({ programs, onOpen }) => {
  const t = useT();
  return (
    <Card data-testid="training-journey">
      <CardContent className="pt-4 space-y-4">
        <CardTitle className="text-base">{t('training:my_journey', 'My training journey')}</CardTitle>
        {programs.map(({ program, sessions, done, total }) => (
          <section key={program.id} className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-medium text-foreground">{program.name}</h3>
              <span className="text-xs text-muted-foreground tabular-nums">{t('training:done_of_total', '{{done}} of {{total}} done', { done, total })}</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden>
              <div className="h-full bg-orange-500" style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} />
            </div>
            <ol className="space-y-1.5">
              {[...sessions].sort((a, b) => (a.sequence - b.sequence) || a.starts_at.localeCompare(b.starts_at)).map((s, i) => (
                <li key={s.id}>
                  <button type="button" onClick={() => onOpen(s)}
                    className="w-full text-left rounded-md border border-border p-2.5 hover:bg-muted/50 transition-colors flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground" aria-hidden>{i + 1}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{s.title}{s.me?.track_name ? <span className="ml-1 font-normal text-xs text-muted-foreground" data-testid="journey-track">· {s.me.track_name}</span> : null}</div>
                        <div className="text-xs text-muted-foreground">{formatSessionRange(s.starts_at, s.ends_at, undefined, s.timezone)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <AssignmentTypeBadge type={s.me?.assignment_type} />
                      {s.me?.attendance ? <AttendanceChip status={s.me.attendance} /> : null}
                      <SessionStatusChip status={s.journey_status} />
                    </div>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </CardContent>
    </Card>
  );
};

export default TrainingJourney;
